import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useTheme } from '../lib/theme';
import SettingsNav, { SECTIONS } from '../components/settings/SettingsNav';
import ProfileSection from '../components/settings/ProfileSection';
import PreferencesSection from '../components/settings/PreferencesSection';
import EmailInboxSection from '../components/settings/EmailInboxSection';
import CategoriesSection from '../components/settings/CategoriesSection';
import AutomationSection from '../components/settings/AutomationSection';
import IntegrationsSection from '../components/settings/IntegrationsSection';
import BillingSection from '../components/settings/BillingSection';
import SupportSection from '../components/settings/SupportSection';

const VALID_KEYS = new Set(SECTIONS.map((s) => s.key));

function parseInitial(location, params) {
  // Hash takes precedence (#profile, #billing, …).
  const hash = (location.hash || '').replace(/^#/, '').trim();
  if (VALID_KEYS.has(hash)) return hash;
  // Backward-compat: `?billing=1` from Phase 23 jumps to the billing tab.
  if (params.get('billing') === '1') return 'billing';
  return 'profile';
}

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { colors } = useTheme();
  const [active, setActive] = useState(() => parseInitial(location, params));
  const [toast, setToast] = useState('');

  // React to external hash changes (e.g. Stripe portal return URL).
  useEffect(() => {
    const next = parseInitial(location, params);
    if (next !== active) setActive(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.search]);

  const onSelect = useCallback((key) => {
    setActive(key);
    navigate(`/app/settings#${key}`, { replace: false });
  }, [navigate]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (msg) setTimeout(() => setToast(''), 2500);
  }, []);

  const section = useMemo(() => {
    switch (active) {
      case 'profile': return <ProfileSection onToast={showToast} />;
      case 'preferences': return <PreferencesSection onToast={showToast} />;
      case 'email': return <EmailInboxSection />;
      case 'categories': return <CategoriesSection />;
      case 'automation': return <AutomationSection />;
      case 'integrations': return <IntegrationsSection onToast={showToast} />;
      case 'billing': return <BillingSection onToast={showToast} autoOpenPicker={params.get('plan') !== null} />;
      case 'support': return <SupportSection onToast={showToast} />;
      default: return null;
    }
  }, [active, params, showToast]);

  return (
    <div data-testid="settings-page" style={{ padding: '24px 20px 48px', maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          type="button" onClick={() => navigate('/app')}
          data-testid="settings-back"
          style={{ display: 'inline-flex', alignItems: 'center', padding: 6, background: 'transparent', border: 'none', color: colors.text, cursor: 'pointer' }}
        >
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: colors.text }}>Settings</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 260px) 1fr', gap: 24, alignItems: 'start' }} className="settings-grid">
        <SettingsNav active={active} onSelect={onSelect} />
        <main style={{ minWidth: 0 }}>{section}</main>
      </div>

      {toast && (
        <div
          data-testid="settings-toast"
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            padding: '10px 18px', borderRadius: 999,
            background: colors.text, color: colors.surface,
            fontSize: 13, fontWeight: 600, zIndex: 1200,
            boxShadow: '0 20px 40px -20px rgba(0,0,0,0.3)',
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @media (max-width: 820px) {
          .settings-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
